/**
 * LYT Communications - Job Import Page
 * Version: 4.1.0
 * Updated: 2026-02-04
 * Route: #job-import
 * 
 * v4.1.0: COMPLETE 4-BUCKET EXTRACTION - Segments + Splices (with billing) +
 *         Unallocated WO items. Fixed: splice billing was being excluded.
 *         Added: WO line items display, unallocated items display, reconciliation.
 *         Fixed: text extraction preserves line breaks for table data.
 * v4.0.0: MAP TILING - Renders map pages at 4.0x scale, then tiles into
 *         legend crop + 6 map section tiles (3x2 grid). Each tile is
 *         ~1175x1584px = under Claude Vision's 1568 native threshold.
 *         Result: 6.9x more detail per section vs single full-page image.
 *         Footage numbers, handhole labels, and splice callouts now readable.
 * v3.3.0: Split processing - WO as text only, map at max quality.
 * v3.0.0: Renders PDF pages to images for Claude Vision API.
 */

import React, { useState, useCallback } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, ArrowLeft, Loader, Trash2, ChevronDown, ChevronUp, Image, Eye } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';

// Configure pdf.js worker
const PDFJS_VERSION = pdfjsLib.version || '4.8.69';
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.mjs`;

// Work order: text-only extraction
const MAX_PAGES_WORKORDER = 10;
// Map: high-res tiled images
const MAX_PAGES_MAP = 4;

// ============================================================
// MAP TILING CONFIG
// ============================================================
// 4.0x scale: 11"x17" page → 4896x3168 (15.5M pixels, under Safari 16M limit)
const MAP_RENDER_SCALE = 2.5; // High res for readability
const MAP_JPEG_QUALITY = 0.70; // Good quality for text readability
// Tile grid: 3 columns x 2 rows = 6 map tiles per page
const TILE_COLS = 2; // Reduced from 3 - fewer tiles, higher quality each
const TILE_ROWS = 2;
// Legend is in the right ~28% of the page, top ~55%
const LEGEND_X_RATIO = 0.72;
const LEGEND_TOP_RATIO = 0.0;
const LEGEND_BOTTOM_RATIO = 0.55;

// Legacy fallbacks
const RENDER_SCALE = 1.5;
const JPEG_QUALITY = 0.55;

function JobImportPage({ darkMode, setDarkMode, user, setCurrentPage }) {
  const [workOrderFile, setWorkOrderFile] = useState(null);
  const [mapFile, setMapFile] = useState(null);
  const [jsonFile, setJsonFile] = useState(null);
  const [rateCardId, setRateCardId] = useState('vexus-la-tx-2026');
  const [processing, setProcessing] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importSource, setImportSource] = useState(null); // 'pdf' or 'json'
  const [error, setError] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [dragOverWork, setDragOverWork] = useState(false);
  const [dragOverMap, setDragOverMap] = useState(false);
  const [dragOverJson, setDragOverJson] = useState(false);

  const bg = darkMode ? '#0d1b2a' : '#ffffff';
  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const borderColor = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const textMuted = darkMode ? '#8892b0' : '#64748b';
  const accent = darkMode ? '#c850c0' : '#0077B6';
  const successGreen = '#28a745';
  const errorRed = '#e85a4f';

  /**
   * Render a single PDF page to a canvas and return the canvas
   */
  const renderPageToCanvas = async (page, scale) => {
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    return canvas;
  };

  /**
   * Crop a region from a canvas and return base64 JPEG
   */
  const cropCanvasToBase64 = (sourceCanvas, x, y, w, h, quality) => {
    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = w;
    cropCanvas.height = h;
    const ctx = cropCanvas.getContext('2d');
    ctx.drawImage(sourceCanvas, x, y, w, h, 0, 0, w, h);
    const dataUrl = cropCanvas.toDataURL('image/jpeg', quality);
    cropCanvas.width = 0;
    cropCanvas.height = 0;
    return dataUrl.split(',')[1];
  };

  /**
   * Tile a rendered map page into legend + grid sections
   * Returns array of { base64, label, index } objects
   */
  const tileMapPage = (canvas, pageNum) => {
    const W = canvas.width;
    const H = canvas.height;
    const tiles = [];

    // 1. Legend crop (right portion, top half)
    const legendX = Math.floor(W * LEGEND_X_RATIO);
    const legendY = Math.floor(H * LEGEND_TOP_RATIO);
    const legendW = W - legendX;
    const legendH = Math.floor(H * LEGEND_BOTTOM_RATIO) - legendY;

    if (legendW > 100 && legendH > 100) {
      tiles.push({
        base64: cropCanvasToBase64(canvas, legendX, legendY, legendW, legendH, MAP_JPEG_QUALITY),
        label: `Page ${pageNum} - LEGEND`,
        index: 0,
      });
      const sizeKB = Math.round(tiles[tiles.length - 1].base64.length * 0.75 / 1024);
      console.log(`Legend tile: ${legendW}x${legendH} = ${sizeKB}KB`);
    }

    // 2. Key Map crop (right portion, bottom half - if exists)
    const keyY = Math.floor(H * LEGEND_BOTTOM_RATIO);
    const keyW = W - legendX;
    const keyH = H - keyY;
    if (keyW > 100 && keyH > 100) {
      tiles.push({
        base64: cropCanvasToBase64(canvas, legendX, keyY, keyW, keyH, MAP_JPEG_QUALITY),
        label: `Page ${pageNum} - KEY MAP`,
        index: 1,
      });
      const sizeKB = Math.round(tiles[tiles.length - 1].base64.length * 0.75 / 1024);
      console.log(`Key Map tile: ${keyW}x${keyH} = ${sizeKB}KB`);
    }

    // 3. Map area tiles (left portion, full height, grid of TILE_COLS x TILE_ROWS)
    const mapW = Math.floor(W * LEGEND_X_RATIO);
    const mapH = H;
    const tileW = Math.floor(mapW / TILE_COLS);
    const tileH = Math.floor(mapH / TILE_ROWS);

    for (let row = 0; row < TILE_ROWS; row++) {
      for (let col = 0; col < TILE_COLS; col++) {
        const tx = col * tileW;
        const ty = row * tileH;
        // Last column/row gets any remaining pixels
        const tw = (col === TILE_COLS - 1) ? (mapW - tx) : tileW;
        const th = (row === TILE_ROWS - 1) ? (mapH - ty) : tileH;

        const tileLabel = `Page ${pageNum} - Section R${row + 1}C${col + 1}`;
        tiles.push({
          base64: cropCanvasToBase64(canvas, tx, ty, tw, th, MAP_JPEG_QUALITY),
          label: tileLabel,
          index: 2 + row * TILE_COLS + col,
        });
        const sizeKB = Math.round(tiles[tiles.length - 1].base64.length * 0.75 / 1024);
        console.log(`${tileLabel}: ${tw}x${th} = ${sizeKB}KB`);
      }
    }

    return tiles;
  };

  /**
   * Process a PDF - extract text and optionally render to images/tiles
   */
  const processPdf = async (file, label, maxPages, opts = {}) => {
    const scale = opts.scale || RENDER_SCALE;
    const quality = opts.quality || JPEG_QUALITY;
    const textOnly = opts.textOnly || false;
    const tileMode = opts.tileMode || false;

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const pageCount = pdf.numPages;
    const pagesToRender = Math.min(pageCount, maxPages);

    const images = [];
    const tileData = []; // For tile mode: array of { base64, label, index }
    let fullText = '';

    for (let i = 1; i <= pagesToRender; i++) {
      setProgressMsg(`${label}: Processing page ${i}/${pagesToRender}...`);
      const page = await pdf.getPage(i);

      // Extract text - preserve line structure for table data
      try {
        const content = await page.getTextContent();
        // Group text items by Y position to preserve rows
        const items = content.items;
        if (items.length > 0) {
          let lastY = null;
          let lineText = '';
          const lines = [];
          for (const item of items) {
            const y = Math.round(item.transform[5]); // Y position
            if (lastY !== null && Math.abs(y - lastY) > 3) {
              // New line (Y position changed)
              if (lineText.trim()) lines.push(lineText.trim());
              lineText = item.str;
            } else {
              // Same line - add with separator
              lineText += (lineText && item.str ? '  ' : '') + item.str;
            }
            lastY = y;
          }
          if (lineText.trim()) lines.push(lineText.trim());
          const pageText = lines.join('\n');
          if (pageText.length > 5) {
            fullText += `\n--- Page ${i} ---\n${pageText}`;
          }
        }
      } catch (e) {
        console.warn(`Text extraction failed for page ${i}:`, e);
      }

      if (textOnly) continue;

      try {
        if (tileMode) {
          // TILE MODE: Render at high res, then crop into tiles
          setProgressMsg(`${label}: Rendering page ${i} at ${scale}x for tiling...`);
          const canvas = await renderPageToCanvas(page, scale);
          console.log(`${label} page ${i}: Full canvas ${canvas.width}x${canvas.height} at ${scale}x`);

          setProgressMsg(`${label}: Tiling page ${i} into legend + ${TILE_COLS * TILE_ROWS} sections...`);
          const pageTiles = tileMapPage(canvas, i);
          tileData.push(...pageTiles);

          // Cleanup the full canvas
          canvas.width = 0;
          canvas.height = 0;
        } else {
          // LEGACY MODE: Single image per page
          const canvas = await renderPageToCanvas(page, scale);
          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          images.push(dataUrl.split(',')[1]);
          const sizeKB = Math.round(images[images.length - 1].length * 0.75 / 1024);
          console.log(`${label} page ${i}: ${sizeKB}KB (${canvas.width}x${canvas.height})`);
          canvas.width = 0;
          canvas.height = 0;
        }
      } catch (renderErr) {
        console.warn(`Page ${i} render failed:`, renderErr);
      }
    }

    if (pageCount > maxPages) {
      fullText += `\n\n[NOTE: PDF has ${pageCount} pages but only first ${maxPages} were processed]`;
    }

    return { images, tiles: tileData, text: fullText.trim(), pageCount };
  };

  // File drop handlers
  const handleDrop = useCallback((e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverWork(false);
    setDragOverMap(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type !== 'application/pdf') {
        setError('Only PDF files are accepted');
        return;
      }
      if (file.size > 25 * 1024 * 1024) {
        setError('File must be under 25MB');
        return;
      }
      setError(null);
      if (type === 'workorder') setWorkOrderFile(file);
      else setMapFile(file);
    }
  }, []);

  const handleFileSelect = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setError('Only PDF files are accepted');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      setError('File must be under 25MB');
      return;
    }
    setError(null);
    if (type === 'workorder') setWorkOrderFile(file);
    else setMapFile(file);
  };

  const handleImport = async () => {
    if (!workOrderFile) {
      setError('Work order PDF is required');
      return;
    }

    setProcessing(true);
    setError(null);
    setImportResult(null);
    setProgressMsg('Starting PDF processing...');

    try {
      // Process work order PDF → TEXT ONLY
      let woText = '';
      try {
        setProgressMsg('Extracting work order text...');
        const woResult = await processPdf(workOrderFile, 'Work Order', MAX_PAGES_WORKORDER, { textOnly: true });
        woText = woResult.text;
        setProgressMsg(`Work order: ${woResult.pageCount} pages, ${woText.length} chars text`);
      } catch (pdfErr) {
        console.error('Work order PDF processing failed:', pdfErr);
        setError(`Failed to process work order PDF: ${pdfErr.message}`);
        setProcessing(false);
        return;
      }

      // Process construction map PDF → HIGH-RES TILED IMAGES
      let mapTiles = [];
      let mapText = '';
      if (mapFile) {
        try {
          setProgressMsg('Rendering construction map at 4.0x for tiling...');
          const mapResult = await processPdf(mapFile, 'Map', MAX_PAGES_MAP, {
            scale: MAP_RENDER_SCALE,
            quality: MAP_JPEG_QUALITY,
            tileMode: true,
          });
          mapTiles = mapResult.tiles;
          mapText = mapResult.text;
          const totalTileKB = mapTiles.reduce((sum, t) => sum + Math.round(t.base64.length * 0.75 / 1024), 0);
          setProgressMsg(`Map: ${mapResult.pageCount} pages → ${mapTiles.length} tiles (${(totalTileKB / 1024).toFixed(1)}MB total)`);
          console.log(`Map tiling complete: ${mapTiles.length} tiles, ${(totalTileKB / 1024).toFixed(1)}MB`);
        } catch (pdfErr) {
          console.warn('Map PDF tiling failed:', pdfErr);
          // Non-fatal
        }
      }

      // Validate
      if (woText.length < 30 && mapTiles.length === 0) {
        setError('No content extracted. Work order needs text, map needs images.');
        setProcessing(false);
        return;
      }

      // Build payload with tiled map images
      const tileImages = mapTiles.map(t => t.base64);
      const tileLabels = mapTiles.map(t => t.label);

      const payload = {
        work_order_text: woText.length > 30 ? woText : undefined,
        map_text: mapText.length > 30 ? mapText : undefined,
        map_images: tileImages.length > 0 ? tileImages : undefined,
        map_tile_labels: tileLabels.length > 0 ? tileLabels : undefined,
        rate_card_id: rateCardId,
      };

      const payloadStr = JSON.stringify(payload);
      const payloadMB = (payloadStr.length / (1024 * 1024)).toFixed(1);
      console.log(`Payload: ${payloadMB}MB (WO text: ${(woText.length / 1024).toFixed(0)}KB + ${tileImages.length} map tiles)`);

      // Size guard - if over Vercel limit, reduce tile count
      if (payloadStr.length > 48 * 1024 * 1024) {
        setProgressMsg(`Payload too large (${payloadMB}MB). Reducing tiles...`);
        // Keep legend + first page tiles only
        const maxTiles = 8;
        if (tileImages.length > maxTiles) {
          payload.map_images = tileImages.slice(0, maxTiles);
          payload.map_tile_labels = tileLabels.slice(0, maxTiles);
          console.log(`Reduced to ${maxTiles} tiles`);
        }
      }

      setProgressMsg(`Sending ${tileImages.length} high-res map tiles + work order to AI (${payloadMB}MB)...`);

      const response = await fetch('/api/pdf-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const errorMsg = errData.message || errData.error || errData.details || `API returned ${response.status}`;
        const errorDetails = errData.stack ? `\n\nStack: ${errData.stack}` : '';
        console.error('Full API error:', errData);
        throw new Error(errorMsg + errorDetails);
      }

      const data = await response.json();

      console.log('API response keys:', Object.keys(data));

      if (data.warning) {
        const preview = (data.raw_response || '').substring(0, 200);
        setError(`AI could not parse extraction. Preview: ${preview || 'empty'}. Check console (F12).`);
        console.warn('Full raw AI response:', data.raw_response);
        setProcessing(false);
        return;
      }

      if (!data.success || !data.extracted) {
        console.error('Unexpected response:', JSON.stringify(data).substring(0, 500));
        throw new Error(`No extraction data returned. Keys: ${Object.keys(data).join(', ')}`);
      }

      setProgressMsg('Building project preview...');

      const ext = data.extracted;
      const jobCode = ext.project?.job_code || null;

      const result = {
        project: {
          job_code: jobCode,
          customer: ext.project?.customer || '',
          project_name: ext.project?.project_name || '',
          po_number: ext.project?.po_number || '',
          wo_total: ext.project?.wo_total || 0,
          rate_card_id: rateCardId,
        },
        work_order_line_items: ext.work_order_line_items || [],
        handholes: ext.handholes || [],
        flowerpots: ext.flowerpots || [],
        ground_rods: ext.ground_rods || [],
        segments: ext.segments || [],
        splice_points: ext.splice_points || [],
        discrepancies: ext.discrepancies || [],
        sections: ext.sections || [],
        counts: ext.counts || {},
        _usage: data.usage,
        _raw: ext,
      };

      setImportResult(result);
      setImportSource('pdf');
      setProgressMsg('');
    } catch (err) {
      setError(`Import failed: ${err.message}`);
      console.error('Import error:', err);
    } finally {
      setProcessing(false);
    }
  };

  // JSON file import — from LYT Extractor output
  const handleJsonImport = async () => {
    if (!jsonFile) return;
    setProcessing(true);
    setError(null);
    setImportResult(null);
    setProgressMsg('Reading JSON file...');

    try {
      const text = await jsonFile.text();
      const data = JSON.parse(text);

      // Validate required keys
      if (!data.segments || !data.structures) {
        throw new Error('Invalid extraction JSON — missing segments or structures');
      }

      // Split structures by type for preview
      const handholes = (data.structures || []).filter(s => s.type === 'handhole' || s.type === 'terminal_box');
      const flowerpots = (data.structures || []).filter(s => s.type === 'flowerpot');
      const groundRods = (data.structures || []).filter(s => s.type === 'ground_rod');

      const totalBore = (data.segments || []).reduce((sum, s) => sum + (s.footage || 0), 0);

      // Build preview result matching the UI shape
      const jobCode = data.project?.name?.match(/^([A-Z]+\.\d+\.\d+)/)?.[1] || data.reconciliation?.notes?.[0]?.match(/^([A-Z]+\.\d+\.\d+)/)?.[1] || 'UNKNOWN';

      const result = {
        project: {
          job_code: jobCode,
          customer: data.project?.client || 'Vexus',
          project_name: data.project?.name || '',
          po_number: data.project?.work_order_number || '',
          wo_total: 0,
          rate_card_id: data.project?.rate_card || rateCardId,
        },
        // Preview arrays (split from unified structures)
        handholes: handholes.map(h => ({
          id: h.id, label: h.size || h.unit_code, type: h.size || '', code: h.unit_code, section: '', street: '',
        })),
        flowerpots: flowerpots.map(f => ({
          id: f.id, label: f.unit_code, code: f.unit_code, section: '', street: '',
        })),
        ground_rods: groundRods.map(g => ({
          id: g.id, label: g.unit_code, code: g.unit_code,
        })),
        segments: (data.segments || []).map(s => ({
          id: s.segment_id, section: '', from_label: '', to_label: '',
          footage: s.footage, street: s.street_name || '',
          bore: { code: `UG${s.duct_count}`, duct_count: s.duct_count },
          pull: { code: 'UG4', cable_type: s.cable_type || '' },
        })),
        splice_points: (data.splice_points || []).map(sp => ({
          id: sp.splice_id, handhole_label: sp.handhole_id || '',
          splice_type: sp.splice_type, position_type: '',
          splitter_count: sp.fiber_count || 0, total_photos_required: 0,
        })),
        work_order_line_items: (data.line_items || []).map(li => ({
          code: li.code, description: li.description, qty: li.quantity, uom: li.uom,
        })),
        discrepancies: [],
        sections: [],
        counts: {
          handholes: handholes.length,
          flowerpots: flowerpots.length,
          ground_rods: groundRods.length,
          segments: (data.segments || []).length,
          splice_points: (data.splice_points || []).length,
          total_bore_footage: totalBore,
          total_pull_footage: 0,
        },
        // Keep the raw extraction data for importProjectFromExtraction
        _extractionData: data,
      };

      setImportResult(result);
      setImportSource('json');
      setProgressMsg('');
    } catch (err) {
      setError(`JSON import failed: ${err.message}`);
      console.error('JSON import error:', err);
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!importResult) return;

    setProcessing(true);
    setError(null);
    try {
      if (importSource === 'json' && importResult._extractionData) {
        // JSON import path — use importProjectFromExtraction
        const { importProjectFromExtraction } = await import('../services/mapService');
        const jobCode = importResult.project.job_code;
        const result = await importProjectFromExtraction(importResult._extractionData, jobCode);

        if (result.success) {
          const c = result.counts;
          alert(`Project imported successfully!\n\nProject: ${jobCode}\nSegments: ${c.segments || 0}\nStructures: ${c.structures || 0}\nSplice Points: ${c.splicePoints || 0}\nLine Items: ${c.lineItems || 0}\n\nRedirecting to project map...`);
          if (setCurrentPage) setCurrentPage('project-map');
        } else {
          const errMsg = result.errors ? result.errors.join('\n') : 'Unknown error';
          setError(`Partial import:\n${errMsg}`);
        }
      } else {
        // PDF import path — use original importProject
        const { importProject } = await import('../services/mapService');
        const jobCode = importResult.project.job_code;
        const result = await importProject(importResult, jobCode);

        if (result.success) {
          const c = result.counts;
          const msg = `Project imported successfully!\n\n` +
            `Project: ${jobCode}\n` +
            `Handholes: ${c.handholes || 0}\n` +
            `Flowerpots: ${c.flowerpots || 0}\n` +
            `Ground Rods: ${c.groundRods || 0}\n` +
            `Segments: ${c.segments || 0}\n` +
            `Splice Points: ${c.splicePoints || 0}\n\n` +
            `Redirecting to project map...`;
          alert(msg);
          if (setCurrentPage) setCurrentPage('project-map');
        } else {
          const errMsg = result.errors ? result.errors.join('\n') : 'Unknown error';
          const c = result.counts || {};
          setError(`Partial import - some items failed:\n${errMsg}\n\nWritten: ${c.handholes || 0} HH, ${c.flowerpots || 0} FP, ${c.groundRods || 0} GR, ${c.segments || 0} SEG, ${c.splicePoints || 0} SP`);
        }
      }
    } catch (err) {
      setError(`Save failed: ${err.message}`);
      console.error('Confirm import error:', err);
    } finally {
      setProcessing(false);
    }
  };

  const DropZone = ({ type, file, setFile, dragOver, setDragOver, label, icon }) => (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => handleDrop(e, type)}
      onClick={() => document.getElementById(`file-${type}`).click()}
      style={{
        border: `2px dashed ${dragOver ? accent : file ? successGreen : borderColor}`,
        borderRadius: '12px',
        padding: '32px 24px',
        textAlign: 'center',
        cursor: 'pointer',
        backgroundColor: dragOver ? (darkMode ? '#1a2f4a' : '#f0f9ff') : (file ? (darkMode ? '#0a2a1a' : '#f0fdf4') : cardBg),
        transition: 'all 0.2s ease',
        flex: 1,
        minWidth: '280px',
      }}
    >
      <input
        id={`file-${type}`}
        type="file"
        accept=".pdf"
        onChange={(e) => handleFileSelect(e, type)}
        style={{ display: 'none' }}
      />
      {file ? (
        <div>
          <CheckCircle size={40} color={successGreen} style={{ marginBottom: '12px' }} />
          <p style={{ color: text, fontWeight: 600, margin: '0 0 4px' }}>{file.name}</p>
          <p style={{ color: textMuted, fontSize: '0.85rem', margin: '0 0 12px' }}>
            {(file.size / 1024 / 1024).toFixed(2)} MB
          </p>
          <button
            onClick={(e) => { e.stopPropagation(); setFile(null); }}
            style={{
              background: 'none',
              border: `1px solid ${errorRed}`,
              color: errorRed,
              padding: '6px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Trash2 size={14} /> Remove
          </button>
        </div>
      ) : (
        <div>
          {icon}
          <p style={{ color: text, fontWeight: 600, margin: '12px 0 4px' }}>{label}</p>
          <p style={{ color: textMuted, fontSize: '0.85rem', margin: 0 }}>
            Drag & drop PDF here or click to browse
          </p>
          <p style={{ color: textMuted, fontSize: '0.75rem', margin: '8px 0 0' }}>
            PDF only, max 25MB
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bg, color: text }}>
      {/* Header */}
      <div style={{
        backgroundColor: darkMode ? '#112240' : '#f1f5f9',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${borderColor}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => setCurrentPage && setCurrentPage('admin-dashboard')}
            style={{ background: 'none', border: 'none', color: accent, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <ArrowLeft size={20} /> Back
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 style={{ margin: 0, fontSize: '1.4rem' }}>
              <span style={{ color: accent }}>Import</span> Work Order
            </h1>
            <span style={{
              backgroundColor: darkMode ? '#1e3a5f' : '#e2e8f0',
              color: darkMode ? '#8892b0' : '#64748b',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '0.75rem',
              fontFamily: 'monospace',
            }}>
              v4.1.0
            </span>
          </div>
        </div>
        <button
          onClick={() => setDarkMode && setDarkMode(!darkMode)}
          aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          style={{ background: 'none', border: 'none', color: textMuted, cursor: 'pointer', fontSize: '1.2rem' }}
        >
          {darkMode ? '☀️' : '🌙'}
        </button>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '960px', margin: '0 auto', padding: '24px' }}>

        {/* Step 1: Upload Files */}
        <div style={{
          backgroundColor: cardBg,
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          border: `1px solid ${borderColor}`,
        }}>
          <h2 style={{ margin: '0 0 8px', fontSize: '1.1rem' }}>
            <Image size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            Step 1: Upload PDFs
          </h2>
          <p style={{ color: textMuted, margin: '0 0 6px', fontSize: '0.9rem' }}>
            Upload the work order and construction map PDFs. Map pages are rendered at <strong>4× resolution</strong> and
            tiled into <strong>8 high-detail sections</strong> so the AI can read footage numbers and handhole labels accurately.
          </p>
          <p style={{ color: accent, margin: '0 0 20px', fontSize: '0.8rem' }}>
            <Eye size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
            High-res tiled Vision AI — reads scanned maps, engineering drawings, and small text
          </p>

          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <DropZone
              type="workorder"
              file={workOrderFile}
              setFile={setWorkOrderFile}
              dragOver={dragOverWork}
              setDragOver={setDragOverWork}
              label="Work Order PDF *"
              icon={<FileText size={40} color={accent} />}
            />
            <DropZone
              type="map"
              file={mapFile}
              setFile={setMapFile}
              dragOver={dragOverMap}
              setDragOver={setDragOverMap}
              label="Construction Map PDF"
              icon={<Upload size={40} color={accent} />}
            />
          </div>

          {/* OR divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '20px 0' }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: borderColor }} />
            <span style={{ color: textMuted, fontSize: '0.85rem', fontWeight: 600 }}>OR</span>
            <div style={{ flex: 1, height: '1px', backgroundColor: borderColor }} />
          </div>

          {/* JSON Import */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOverJson(true); }}
            onDragLeave={() => setDragOverJson(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOverJson(false);
              const file = e.dataTransfer.files?.[0];
              if (file && file.name.endsWith('.json')) setJsonFile(file);
              else setError('Only .json files accepted');
            }}
            onClick={() => document.getElementById('file-json').click()}
            style={{
              border: `2px dashed ${dragOverJson ? accent : jsonFile ? successGreen : borderColor}`,
              borderRadius: '12px',
              padding: '24px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: dragOverJson ? (darkMode ? '#1a2f4a' : '#f0f9ff') : (jsonFile ? (darkMode ? '#0a2a1a' : '#f0fdf4') : cardBg),
              transition: 'all 0.2s ease',
            }}
          >
            <input
              id="file-json"
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) { setJsonFile(file); setError(null); }
              }}
            />
            {jsonFile ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                <CheckCircle size={24} color={successGreen} />
                <span style={{ color: successGreen, fontWeight: 600 }}>{jsonFile.name}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); setJsonFile(null); }}
                  style={{ background: 'none', border: 'none', color: errorRed, cursor: 'pointer', padding: '4px' }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ) : (
              <div>
                <FileText size={32} color={accent} />
                <p style={{ color: text, fontWeight: 600, margin: '8px 0 4px' }}>LYT Extractor JSON</p>
                <p style={{ color: textMuted, fontSize: '0.85rem', margin: 0 }}>
                  Drop an _extracted.json file from the LYT Extractor tool
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Step 2: Rate Card Selection */}
        <div style={{
          backgroundColor: cardBg,
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          border: `1px solid ${borderColor}`,
        }}>
          <h2 style={{ margin: '0 0 8px', fontSize: '1.1rem' }}>
            Step 2: Select Rate Card
          </h2>
          <select
            value={rateCardId}
            onChange={(e) => setRateCardId(e.target.value)}
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: '10px 14px',
              borderRadius: '8px',
              border: `1px solid ${borderColor}`,
              backgroundColor: darkMode ? '#0d1b2a' : '#ffffff',
              color: text,
              fontSize: '0.95rem',
            }}
          >
            <option value="vexus-la-tx-2026">Vexus LA/TX 2026</option>
            <option value="highline-tx-2026">Highline TX 2026</option>
            <option value="ies-dfw-2026">IE Smart Systems DFW 2026</option>
          </select>
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            backgroundColor: darkMode ? '#2a0a0a' : '#fef2f2',
            border: `1px solid ${errorRed}`,
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
          }}>
            <AlertCircle size={20} color={errorRed} style={{ flexShrink: 0, marginTop: '2px' }} />
            <span style={{ color: errorRed, whiteSpace: 'pre-wrap' }}>{error}</span>
          </div>
        )}

        {/* Process Buttons */}
        {!importResult && (
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <button
              onClick={handleImport}
              disabled={!workOrderFile || processing}
              style={{
                backgroundColor: (!workOrderFile || processing) ? '#6c757d' : accent,
                color: '#ffffff',
                border: 'none',
                padding: '14px 32px',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: (!workOrderFile || processing) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flex: 1,
                justifyContent: 'center',
              }}
            >
              {processing && !jsonFile ? (
                <>
                  <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  {progressMsg || 'Processing...'}
                </>
              ) : (
                <>
                  <Eye size={20} />
                  Extract via Vision AI
                </>
              )}
            </button>
            <button
              onClick={handleJsonImport}
              disabled={!jsonFile || processing}
              style={{
                backgroundColor: (!jsonFile || processing) ? '#6c757d' : successGreen,
                color: '#ffffff',
                border: 'none',
                padding: '14px 32px',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: (!jsonFile || processing) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flex: 1,
                justifyContent: 'center',
              }}
            >
              {processing && jsonFile ? (
                <>
                  <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  {progressMsg || 'Loading...'}
                </>
              ) : (
                <>
                  <FileText size={20} />
                  Import from Extractor JSON
                </>
              )}
            </button>
          </div>
        )}

        {/* Progress indicator */}
        {processing && progressMsg && (
          <div style={{
            backgroundColor: darkMode ? '#0a1628' : '#eff6ff',
            border: `1px solid ${accent}40`,
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '24px',
            textAlign: 'center',
          }}>
            <p style={{ color: accent, margin: 0, fontSize: '0.9rem' }}>
              <Loader size={14} style={{ verticalAlign: 'middle', marginRight: '8px', animation: 'spin 1s linear infinite' }} />
              {progressMsg}
            </p>
          </div>
        )}

        {/* Step 3: Import Preview */}
        {importResult && (
          <div style={{
            backgroundColor: cardBg,
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            border: `1px solid ${successGreen}`,
          }}>
            <h2 style={{ margin: '0 0 16px', fontSize: '1.1rem', color: successGreen }}>
              <CheckCircle size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
              Step 3: Review Extracted Data
            </h2>

            {importResult._usage && (
              <p style={{ color: textMuted, fontSize: '0.75rem', margin: '0 0 16px' }}>
                AI tokens: {importResult._usage.input_tokens?.toLocaleString()} in / {importResult._usage.output_tokens?.toLocaleString()} out
              </p>
            )}

            {/* Project Summary */}
            <div style={{
              backgroundColor: darkMode ? '#0d1b2a' : '#ffffff',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
              border: `1px solid ${borderColor}`,
            }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '1rem' }}>Project Details</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                {[
                  ['Job Code', importResult.project.job_code],
                  ['Customer', importResult.project.customer],
                  ['Project Name', importResult.project.project_name],
                  ['PO Number', importResult.project.po_number],
                  ['WO Total', importResult.project.wo_total ? `$${Number(importResult.project.wo_total).toLocaleString()}` : '—'],
                  ['Rate Card', importResult.project.rate_card_id],
                  ['Sections', (importResult.sections || []).join(', ') || '—'],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p style={{ color: textMuted, fontSize: '0.8rem', margin: '0 0 2px' }}>{label}</p>
                    <p style={{ margin: 0, fontWeight: 500 }}>{value || '—'}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {[
                ['Handholes', importResult.counts.handholes || importResult.handholes.length, accent],
                ['Flowerpots', importResult.counts.flowerpots || importResult.flowerpots.length, '#FF9800'],
                ['Ground Rods', importResult.counts.ground_rods || importResult.ground_rods.length, '#795548'],
                ['Segments', importResult.counts.segments || importResult.segments.length, '#2196F3'],
                ['Splice Points', importResult.counts.splice_points || importResult.splice_points.length, '#4CAF50'],
                ['Bore Footage', `${(importResult.counts.total_bore_footage || 0).toLocaleString()} LF`, '#FFB800'],
                ['Pull Footage', `${(importResult.counts.total_pull_footage || 0).toLocaleString()} LF`, '#E91E63'],
              ].map(([label, value, color]) => (
                <div key={label} style={{
                  backgroundColor: darkMode ? '#0d1b2a' : '#ffffff',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  flex: '1',
                  minWidth: '120px',
                  textAlign: 'center',
                  border: `1px solid ${borderColor}`,
                }}>
                  <p style={{ fontSize: '1.2rem', fontWeight: 700, color, margin: '0 0 4px' }}>{value}</p>
                  <p style={{ color: textMuted, fontSize: '0.75rem', margin: 0 }}>{label}</p>
                </div>
              ))}
            </div>

            {/* Segments */}
            <div style={{
              backgroundColor: darkMode ? '#0d1b2a' : '#ffffff',
              borderRadius: '8px',
              border: `1px solid ${borderColor}`,
              marginBottom: '16px',
            }}>
              <button
                onClick={() => setExpandedSection(expandedSection === 'segments' ? null : 'segments')}
                style={{
                  width: '100%', padding: '14px 16px', background: 'none', border: 'none',
                  color: text, cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', fontSize: '1rem', fontWeight: 600,
                }}
              >
                Segments ({importResult.segments.length})
                {expandedSection === 'segments' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {expandedSection === 'segments' && (
                <div style={{ padding: '0 16px 16px', overflowX: 'auto' }}>
                  {importResult.segments.length === 0 ? (
                    <p style={{ color: textMuted, fontStyle: 'italic' }}>No segments extracted.</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                          {['ID', 'Section', 'From', 'To', 'Footage', 'Bore', 'Pull', 'Street'].map(h => (
                            <th key={h} style={{ padding: '8px', textAlign: 'left', color: textMuted, fontWeight: 500 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.segments.map((seg, i) => (
                          <tr key={seg.id || i} style={{ borderBottom: `1px solid ${borderColor}` }}>
                            <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '0.8rem' }}>{seg.id}</td>
                            <td style={{ padding: '8px' }}>{seg.section}</td>
                            <td style={{ padding: '8px' }}>{seg.from_label}</td>
                            <td style={{ padding: '8px' }}>{seg.to_label}</td>
                            <td style={{ padding: '8px', fontWeight: 600 }}>{seg.footage} LF</td>
                            <td style={{ padding: '8px', fontFamily: 'monospace' }}>{seg.bore?.code} x{seg.bore?.duct_count}</td>
                            <td style={{ padding: '8px', fontFamily: 'monospace' }}>{seg.pull?.code} ({seg.pull?.cable_type})</td>
                            <td style={{ padding: '8px' }}>{seg.street}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>

            {/* Splice Points */}
            <div style={{
              backgroundColor: darkMode ? '#0d1b2a' : '#ffffff',
              borderRadius: '8px',
              border: `1px solid ${borderColor}`,
              marginBottom: '16px',
            }}>
              <button
                onClick={() => setExpandedSection(expandedSection === 'splices' ? null : 'splices')}
                style={{
                  width: '100%', padding: '14px 16px', background: 'none', border: 'none',
                  color: text, cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', fontSize: '1rem', fontWeight: 600,
                }}
              >
                Splice Points ({importResult.splice_points.length}) — tracking only (no billing)
                {expandedSection === 'splices' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {expandedSection === 'splices' && (
                <div style={{ padding: '0 16px 16px', overflowX: 'auto' }}>
                  {importResult.splice_points.length === 0 ? (
                    <p style={{ color: textMuted, fontStyle: 'italic' }}>No splice points extracted.</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                          {['ID', 'Handhole', 'Type', 'Position', 'Splitters', 'PM Readings', 'Photos'].map(h => (
                            <th key={h} style={{ padding: '8px', textAlign: 'left', color: textMuted, fontWeight: 500 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.splice_points.map((sp, i) => (
                          <tr key={sp.id || i} style={{ borderBottom: `1px solid ${borderColor}` }}>
                            <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '0.8rem' }}>{sp.id}</td>
                            <td style={{ padding: '8px', fontWeight: 600 }}>{sp.handhole_label}</td>
                            <td style={{ padding: '8px' }}>{sp.splice_type}</td>
                            <td style={{ padding: '8px' }}>{sp.position_type}</td>
                            <td style={{ padding: '8px' }}>{sp.splitter_count}</td>
                            <td style={{ padding: '8px' }}>{sp.splice_type === '1x4' ? '8' : '0'}</td>
                            <td style={{ padding: '8px' }}>{sp.total_photos_required}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>

            {/* Work Order Line Items */}
            <div style={{
              backgroundColor: darkMode ? '#0d1b2a' : '#ffffff',
              borderRadius: '8px',
              border: `1px solid ${borderColor}`,
              marginBottom: '16px',
            }}>
              <button
                onClick={() => setExpandedSection(expandedSection === 'wolines' ? null : 'wolines')}
                style={{
                  width: '100%', padding: '14px 16px', background: 'none', border: 'none',
                  color: text, cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', fontSize: '1rem', fontWeight: 600,
                }}
              >
                WO Line Items ({(importResult.work_order_line_items || []).length})
                {expandedSection === 'wolines' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {expandedSection === 'wolines' && (
                <div style={{ padding: '0 16px 16px', overflowX: 'auto' }}>
                  {(importResult.work_order_line_items || []).length === 0 ? (
                    <p style={{ color: textMuted, fontStyle: 'italic' }}>No work order line items extracted.</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                          {['Code', 'Description', 'Qty', 'UOM'].map(h => (
                            <th key={h} style={{ padding: '8px', textAlign: 'left', color: textMuted, fontWeight: 500 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(importResult.work_order_line_items || []).map((li, i) => (
                          <tr key={i} style={{ borderBottom: `1px solid ${borderColor}` }}>
                            <td style={{ padding: '8px', fontFamily: 'monospace', fontWeight: 600 }}>{li.code}</td>
                            <td style={{ padding: '8px' }}>{li.description}</td>
                            <td style={{ padding: '8px' }}>{(li.qty || 0).toLocaleString()}</td>
                            <td style={{ padding: '8px' }}>{li.uom}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>

            {/* Handholes */}
            <div style={{
              backgroundColor: darkMode ? '#0d1b2a' : '#ffffff',
              borderRadius: '8px',
              border: `1px solid ${borderColor}`,
              marginBottom: '16px',
            }}>
              <button
                onClick={() => setExpandedSection(expandedSection === 'handholes' ? null : 'handholes')}
                style={{
                  width: '100%', padding: '14px 16px', background: 'none', border: 'none',
                  color: text, cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', fontSize: '1rem', fontWeight: 600,
                }}
              >
                Handholes ({importResult.handholes.length})
                {expandedSection === 'handholes' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {expandedSection === 'handholes' && (
                <div style={{ padding: '0 16px 16px', overflowX: 'auto' }}>
                  {importResult.handholes.length === 0 ? (
                    <p style={{ color: textMuted, fontStyle: 'italic' }}>No handholes extracted.</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                          {['ID', 'Label', 'Type', 'Code', 'Section', 'Street'].map(h => (
                            <th key={h} style={{ padding: '8px', textAlign: 'left', color: textMuted, fontWeight: 500 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.handholes.map((hh, i) => (
                          <tr key={hh.id || i} style={{ borderBottom: `1px solid ${borderColor}` }}>
                            <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '0.8rem' }}>{hh.id}</td>
                            <td style={{ padding: '8px', fontWeight: 600 }}>{hh.label}</td>
                            <td style={{ padding: '8px' }}>{hh.type}</td>
                            <td style={{ padding: '8px', fontFamily: 'monospace' }}>{hh.code}</td>
                            <td style={{ padding: '8px' }}>{hh.section}</td>
                            <td style={{ padding: '8px' }}>{hh.street}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>

            {/* Flowerpots */}
            <div style={{
              backgroundColor: darkMode ? '#0d1b2a' : '#ffffff',
              borderRadius: '8px',
              border: `1px solid ${borderColor}`,
              marginBottom: '16px',
            }}>
              <button
                onClick={() => setExpandedSection(expandedSection === 'flowerpots' ? null : 'flowerpots')}
                style={{
                  width: '100%', padding: '14px 16px', background: 'none', border: 'none',
                  color: text, cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', fontSize: '1rem', fontWeight: 600,
                }}
              >
                Flowerpots ({importResult.flowerpots.length})
                {expandedSection === 'flowerpots' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {expandedSection === 'flowerpots' && (
                <div style={{ padding: '0 16px 16px', overflowX: 'auto' }}>
                  {importResult.flowerpots.length === 0 ? (
                    <p style={{ color: textMuted, fontStyle: 'italic' }}>No flowerpots extracted.</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                          {['ID', 'Label', 'Code', 'Section', 'Street'].map(h => (
                            <th key={h} style={{ padding: '8px', textAlign: 'left', color: textMuted, fontWeight: 500 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.flowerpots.map((fp, i) => (
                          <tr key={fp.id || i} style={{ borderBottom: `1px solid ${borderColor}` }}>
                            <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '0.8rem' }}>{fp.id}</td>
                            <td style={{ padding: '8px', fontWeight: 600 }}>{fp.label}</td>
                            <td style={{ padding: '8px', fontFamily: 'monospace' }}>{fp.code}</td>
                            <td style={{ padding: '8px' }}>{fp.section}</td>
                            <td style={{ padding: '8px' }}>{fp.street}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>

            {/* Discrepancies */}
            {importResult.discrepancies.length > 0 && (
              <div style={{
                backgroundColor: darkMode ? '#2a2a0a' : '#fffbeb',
                borderRadius: '8px',
                border: `1px solid #FFB800`,
                marginBottom: '16px',
                padding: '16px',
              }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '0.95rem', color: '#FFB800' }}>
                  Discrepancies ({importResult.discrepancies.length})
                </h3>
                {importResult.discrepancies.map((d, i) => (
                  <p key={i} style={{ margin: '4px 0', fontSize: '0.85rem', color: text }}>
                    <strong>{d.item}:</strong> WO says {d.wo_qty}, map shows {d.map_qty} — {d.action}
                  </p>
                ))}
              </div>
            )}

            {/* Confirm / Cancel */}
            <div style={{ display: 'flex', gap: '16px' }}>
              <button
                onClick={handleConfirmImport}
                disabled={processing}
                style={{
                  backgroundColor: successGreen, color: '#ffffff', border: 'none',
                  padding: '14px 32px', borderRadius: '8px', fontSize: '1rem', fontWeight: 600,
                  cursor: processing ? 'not-allowed' : 'pointer', flex: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                }}
              >
                {processing ? (
                  <><Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /> Saving...</>
                ) : (
                  <><CheckCircle size={20} /> Confirm & Create Project</>
                )}
              </button>
              <button
                onClick={() => { setImportResult(null); setImportSource(null); setWorkOrderFile(null); setMapFile(null); setJsonFile(null); }}
                style={{
                  backgroundColor: 'transparent', color: errorRed, border: `1px solid ${errorRed}`,
                  padding: '14px 24px', borderRadius: '8px', fontSize: '1rem', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <div style={{ position: 'fixed', bottom: '4px', right: '8px', fontSize: '0.6rem', color: 'transparent', userSelect: 'none' }}
        onDoubleClick={(e) => { e.target.style.color = textMuted; }}
      >
        JobImportPage v4.1.0
      </div>
    </div>
  );
}

export default JobImportPage;
